import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import type { JwtToken } from '../types/types'
import useAccess from './access'
import useLocalStorage from './useLocalStorage'

import useStore from './store'
import useGlobalConfig from './globalConfig'
import useLoyalty from './loyalty'
import type { Store } from '@/views/contests/Components/types/global'

export default function useAuth() {
  const {
    merchantName,
    merchantLogo,
    stores,
    hasWallet,
    hasAllPartnersWithSegmentationSupport,
    customerTags,
    frequencyTypes,
    frequencyTypesSegments,
    displayBusinessDashboard,
    permissions,
    permissionsByStores,
    subscriptions,
    sms,
    jwt,
    user,
    userHasClients,
    languages,
    globalConfig,
    moduleAccesses,
    moduleAccessesByStores
  } = useLocalStorage()
  const { closeNotificationModal, create } = useNotification()
  const { userMergePermissions, userMergeSubscriptions, userMergeModules } = useAccess()
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const signUpFinalStep = 2
  const { objectiveSlug } = useStore()
  const { getKeys } = useGlobalConfig()
  const { loyaltyRouteName } = useLoyalty()

  const login = async () => {
    closeNotificationModal()

    const exitLogin = userStep.value < signUpFinalStep
    if (exitLogin) {
      return
    }

    const [requestUserPermissions, requestUserSubscriptions, requestUserModules] = await Promise.all([
      axios.get('users/permissions'),
      axios.get('users/subscriptions'),
      axios.get('users/modules')
    ])

    permissions.value = userMergePermissions(requestUserPermissions.data.data)
    permissionsByStores.value = requestUserPermissions.data.data

    subscriptions.value = userMergeSubscriptions(requestUserSubscriptions.data.data)

    moduleAccesses.value = userMergeModules(requestUserModules.data.data)
    moduleAccessesByStores.value = requestUserModules.data.data

    await setRequiredFields()
    getConfigs()
  }

  const setRequiredFields = async () => {
    setGlobalConfig()
  }

  const getConfigs = async () => {
    requestMerchantStores().then((response) => (stores.value = response))

    requestMerchantLogo().then((response) => (merchantLogo.value = response.data.data.logo))

    requestCustomerTags().then((response) => (customerTags.value = response.data.data))

    requestFrequencyTypes().then((response) => (frequencyTypes.value = response.data.data))
    requestFrequencyTypesSegments().then(async (response) => (frequencyTypesSegments.value = response.data.data))

    requestDisplayBusinessDashboard().then((response) => (displayBusinessDashboard.value = response.data.hasOrders))

    requestWallets().then((result) => (hasWallet.value = result))

    requestMerchantInformations().then((response) => {
      merchantName.value = response.data.data.name
      hasAllPartnersWithSegmentationSupport.value = response.data.data.hasAllPartnersWithSegmentationSupport
    })

    requestAvailableLanguages().then((response) => {
      languages.value = response.data.data.map((language) => ({
        id: language.id,
        nativeName: language.nativeName.charAt(0).toUpperCase() + language.nativeName.slice(1),
        flag: language.flag,
        iso639_1: language.iso639_1,
        code: language.code
      }))
    })

    requestMerchantSmsPrices().then((response) => {
      sms.value.price = parseFloat(response.data.data[0]?.sellingSmsPriceWithoutVat ?? 9)
      sms.value.countryId = response.data.data[0]?.country.id ?? 78
    })

    requestClients().then((response) => {
      userHasClients.value = response.data.data.length > 0
    })
  }

  const requestMerchantLogo = async () => {
    return await axios.get(`merchants/${user.value.merchant.id}/media`)
  }
  const requestMerchantInformations = async () => await axios.get(`merchants/${user.value.merchant.id}`)

  const requestAvailableLanguages = async () => await axios.get('languages/available')
  const requestCustomerTags = async () => await axios.get('customers/tags')
  const requestFrequencyTypes = async () => await axios.get('frequency-types')
  const requestFrequencyTypesSegments = async () =>
    await axios.get(`/merchants/${user.value.merchant.id}/segments`, {
      params: {
        isFrequencyType: 1
      }
    })

  const requestDisplayBusinessDashboard = async () => await axios.get('/users/check-orders')

  const requestMerchantSmsPrices = async () => {
    return await axios.get(`merchants/${user.value.merchant.id}/sms-prices`, {
      params: { isLimitedToCustomerMerchants: 1 }
    })
  }

  const requestClients = async () => {
    return await axios.get(`clients`)
  }

  const requestMerchantStores = async (): Promise<Store[]> => {
    const { data } = await axios.get('stores', { params: { merchantId: user.value.merchant.id } })
    return data.data
  }

  const requestWallets = async () => {
    try {
      const response = await axios.get(`merchants/${user.value.merchant.id}/wallets`)
      return response.data.data.length > 0
    } catch {
      return false
    }
  }

  const setGlobalConfig = async () => {
    const merchantKeys = await getKeys('merchants')
    globalConfig.value.merchant = merchantKeys.data
  }

  const refreshToken = async () => {
    const baseURL: string = import.meta.env.VITE_API_BASEURL
    const newAxiosInstance = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${jwt.value}`
      }
    })
    const authRefresh = await newAxiosInstance.post<JwtToken>('auth/refresh')
    jwt.value = authRefresh.data.jwt.accessToken
    await login()
  }

  const loginWithLang = async () => {
    try {
      await axios.patch(`/users/${user.value.id}/language`, { language: route.query.lang })
      await login()
      const { data } = await axios.get('auth/me')
      user.value = data.data
    } catch (error) {
      create('error', { text: `${t('global.languages.error.update')} (${error.response.status})` })
    } finally {
      await router.push({ name: 'Dashboard' })
    }
  }

  const userStep = computed(() => {
    const findStep = Number(user.value?.configs?.['signupStep'])

    const userAlreadySignedUp = (!findStep && !!user.value.id) || findStep === 2
    if (userAlreadySignedUp) {
      return signUpFinalStep
    }
    return findStep ? Number(findStep) + 1 : 1
  })

  const logInSignUpUser = async () => {
    await new Promise((resolve) => {
      login().then(() => {
        axios.get('auth/me').then(({ data }) => {
          user.value = data.data
          setTimeout(resolve, 2500)
        })
      })
    })
    handleLoginRedirection()
  }

  const handleLoginRedirection = () => {
    const objective = objectiveSlug.value
    const hasGooglePlaceId = stores.value.find((store) => store.googlePlaceId)
    let routeName

    switch (objective) {
      case 'loyalty-program':
        routeName = loyaltyRouteName.value
        break
      case 'google':
        routeName = hasGooglePlaceId ? 'Home' : 'ContestList'
        break
      default:
        routeName = 'Home'
    }

    router.push({ name: routeName })
  }

  const authUserWithRouteToken = async () => {
    if (!route.query?.token) {
      return
    }
    try {
      jwt.value = route.query?.token.toString()
      const { data } = await axios.get('auth/me')
      user.value = data.data
      await login()
      router.push({ name: 'Home' })
    } catch {
      create('error', { text: t('notifications.submit'), timeout: 5000 })
    }
  }

  return {
    login,
    handleLoginRedirection,
    refreshToken,
    loginWithLang,
    userStep,
    authUserWithRouteToken,
    logInSignUpUser
  }
}
